/**
 * Local-clock day / afternoon / sunset / night presentation.
 * Visual only: no save field and no economy / gameplay mutation.
 */

export const TIME_OF_DAY_BANDS = ['dawn', 'day', 'sunset', 'night'] as const;
export type TimeOfDayBand = (typeof TIME_OF_DAY_BANDS)[number];

export const TOD_QUERY_KEYS = ['tod', 'time'] as const;

/** Inclusive start hour → exclusive end hour in local time. Night wraps midnight. */
export const TIME_OF_DAY_HOURS: Record<TimeOfDayBand, { startHour: number; endHour: number }> = {
  dawn: { startHour: 6, endHour: 8 },
  day: { startHour: 8, endHour: 17 },
  sunset: { startHour: 17, endHour: 20 },
  night: { startHour: 20, endHour: 6 },
};

export function parseTimeOfDayOverride(search: string): TimeOfDayBand | null {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  for (const key of TOD_QUERY_KEYS) {
    const raw = params.get(key);
    if (!raw) {
      continue;
    }
    const value = raw.trim().toLowerCase();
    if (value === 'morning') {
      return 'dawn';
    }
    if (value === 'afternoon' || value === 'dusk') {
      return 'sunset';
    }
    if ((TIME_OF_DAY_BANDS as readonly string[]).includes(value)) {
      return value as TimeOfDayBand;
    }
  }
  return null;
}

export function timeOfDayBandFromHour(hour: number): TimeOfDayBand {
  const wrapped = ((hour % 24) + 24) % 24;
  if (wrapped >= 6 && wrapped < 8) {
    return 'dawn';
  }
  if (wrapped >= 8 && wrapped < 17) {
    return 'day';
  }
  if (wrapped >= 17 && wrapped < 20) {
    return 'sunset';
  }
  return 'night';
}

export function resolveTimeOfDayBand(now: Date, search = ''): TimeOfDayBand {
  return parseTimeOfDayOverride(search) ?? timeOfDayBandFromHour(now.getHours() + now.getMinutes() / 60);
}

export interface TimeOfDayVisual {
  band: TimeOfDayBand;
  skyTop: number;
  skyBottom: number;
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  sunColor: number;
  sunIntensity: number;
  sunPosition: { x: number; y: number; z: number };
  rimColor: number;
  rimIntensity: number;
  ambientColor: number;
  ambientIntensity: number;
  environmentIntensity: number;
  exposure: number;
  lampEmissive: number;
  /** COL-011B: emissive strength of attraction bulb strings (palette `lights` channel). */
  lightsEmissive: number;
  /** COL-011B: opacity of additive bulb/lamp halos (0 hides them entirely). */
  glowOpacity: number;
  /** COL-011B: opacity of the warm ground pools under lamps and lit attractions. */
  poolOpacity: number;
  /** COL-011B: emissive lift of the festive floor tile so premium paths sparkle at night. */
  festiveEmissive: number;
}

const LOOK: Record<TimeOfDayBand, TimeOfDayVisual> = {
  dawn: {
    band: 'dawn',
    skyTop: 0x8eb6d4,
    skyBottom: 0xf3c9a0,
    hemiSky: 0xc8ddf4,
    hemiGround: 0xc49a6a,
    hemiIntensity: 0.58,
    sunColor: 0xffd0a8,
    sunIntensity: 1.35,
    sunPosition: { x: -12, y: 10, z: 6 },
    rimColor: 0xffc4a0,
    rimIntensity: 0.28,
    ambientColor: 0xffd8b8,
    ambientIntensity: 0.2,
    environmentIntensity: 0.26,
    exposure: 0.96,
    lampEmissive: 0.45,
    lightsEmissive: 0.9,
    glowOpacity: 0.22,
    poolOpacity: 0.1,
    festiveEmissive: 0.05,
  },
  day: {
    band: 'day',
    skyTop: 0xbfe1f6,
    skyBottom: 0xffdfbc,
    hemiSky: 0xd6ecff,
    hemiGround: 0xb98e5c,
    hemiIntensity: 0.7,
    sunColor: 0xfff0d2,
    sunIntensity: 1.95,
    sunPosition: { x: -9.5, y: 16.5, z: 8.5 },
    rimColor: 0xbfd8ff,
    rimIntensity: 0.32,
    ambientColor: 0xffe6c8,
    ambientIntensity: 0.22,
    environmentIntensity: 0.32,
    exposure: 1.0,
    lampEmissive: 0.35,
    lightsEmissive: 0.85,
    glowOpacity: 0,
    poolOpacity: 0,
    festiveEmissive: 0,
  },
  sunset: {
    band: 'sunset',
    skyTop: 0x4a6a9a,
    skyBottom: 0xf08a4a,
    hemiSky: 0xf0b070,
    hemiGround: 0x8a4a30,
    hemiIntensity: 0.52,
    sunColor: 0xff9a58,
    sunIntensity: 1.15,
    sunPosition: { x: 12, y: 7.5, z: -6 },
    rimColor: 0xff7a40,
    rimIntensity: 0.42,
    ambientColor: 0xffc090,
    ambientIntensity: 0.18,
    environmentIntensity: 0.22,
    exposure: 0.92,
    lampEmissive: 1.4,
    lightsEmissive: 1.5,
    glowOpacity: 0.5,
    poolOpacity: 0.28,
    festiveEmissive: 0.18,
  },
  /**
   * COL-011B night: deeper indigo sky and cooler, dimmer fill so the warm lamps, bulb strings
   * and halos carry the scene — while sun/ambient stay above the readability floor.
   */
  night: {
    band: 'night',
    skyTop: 0x0f1a36,
    skyBottom: 0x243052,
    hemiSky: 0x4d6aa8,
    hemiGround: 0x232a40,
    hemiIntensity: 0.3,
    sunColor: 0xa8bce8,
    sunIntensity: 0.42,
    sunPosition: { x: -6, y: 18, z: 10 },
    rimColor: 0x7aa0d8,
    rimIntensity: 0.18,
    ambientColor: 0x7e94c8,
    ambientIntensity: 0.21,
    environmentIntensity: 0.08,
    exposure: 0.74,
    lampEmissive: 3.2,
    lightsEmissive: 2.6,
    glowOpacity: 0.9,
    poolOpacity: 0.6,
    festiveEmissive: 0.4,
  },
};

export function timeOfDayVisual(band: TimeOfDayBand): TimeOfDayVisual {
  return LOOK[band];
}

export function hexCss(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}
